import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useFlowColors } from '../useFlowColors';
import { AndonSummary, ProcessPathCard, SupportPathMetrics } from './model';
import {
    ACTUAL_VS_EXPECTED_LEGEND,
    ANDON_DIMENSIONS,
    AndonDimension,
    AndonDimensionKey,
    AndonEntry,
    BUFFER_STATE_ARROWS,
    BUFFER_STATE_LABELS,
    andonEntries,
    bufferFillPercent,
    formatFlowNumber,
    formatHealthPercentage,
    formatSignedNumber,
    formatVariance,
    getStatusLabel,
    isAboveThresholdStatus,
    summaryStatus,
} from './utils';

export interface AndonCardsViewProps {
    /** Per-path Andon cards. Optional on the service contract; empty is handled. */
    cards?: ProcessPathCard[];
    /** Top-level Andon roll-up. Each dimension is optional; a missing one has no tile. */
    andonSummary?: AndonSummary;
    /** Support paths — headcount-only, so they appear in the Staffing section alone. */
    supportPaths?: SupportPathMetrics[];
}

/**
 * Entry card width bounds, in CSS pixels. Cards grow to share a row — so a section's cards span
 * the page, as on the prototype — but never shrink below the minimum (which fits a name like
 * "AMAZON RESALE TECH" on two lines) or stretch past the maximum on a short last row.
 */
const ENTRY_CARD_MIN_WIDTH = 150;
const ENTRY_CARD_MAX_WIDTH = 230;

// Colours come from useFlowColors (Meridian theme tokens), so the view follows dark mode: the
// selected tile is outlined in the primary colour, tracks and tile borders are hairlines, and
// section headers sit on the highlight surface.

/**
 * One dimension's summary tile: the healthy share and "X of Y paths healthy".
 *
 * The share is coloured by the worst status in the roll-up (`summaryStatus`) — the same
 * colouring the section's cards use — and the counts underneath carry the meaning in text.
 *
 * The tile is a toggle button: pressing it narrows the view to that dimension's section, and
 * pressing it again shows every section. `aria-pressed` carries the state for assistive tech;
 * the outline carries it visually.
 */
const DimensionTile: React.FC<{
    dimension: AndonDimension;
    andonSummary: AndonSummary;
    selected: boolean;
    onToggle: () => void;
}> = ({ dimension, andonSummary, selected, onToggle }) => {
    const colors = useFlowColors();
    const summary = andonSummary[dimension.key];
    if (summary == null) return null;
    const color = colors.statusAccent(summaryStatus(summary));
    return (
        <button
            type="button"
            aria-pressed={selected}
            onClick={onToggle}
            data-testid={`andon-tile-${dimension.key}`}
            style={{
                flex: '1 1 0',
                minWidth: 180,
                background: colors.surface,
                border: selected ? `2px solid ${colors.primary}` : `1px solid ${colors.hairline}`,
                // Keep the content still when the outline thickens.
                padding: selected ? 15 : 16,
                borderRadius: 4,
                cursor: 'pointer',
                textAlign: 'center',
                font: 'inherit',
            }}
        >
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: colors.inkSubdued }}>
                <span aria-hidden="true">{dimension.icon} </span>
                {dimension.label.toUpperCase()}
            </div>
            <div
                style={{ fontSize: 28, fontWeight: 700, color, margin: '4px 0' }}
                data-testid={`andon-tile-${dimension.key}-health`}
            >
                {formatHealthPercentage(summary.healthPercentage)}
            </div>
            <div style={{ fontSize: 12, color: colors.inkSubdued }}>
                {formatFlowNumber(summary.healthyPaths)} of {formatFlowNumber(summary.totalPaths)} paths healthy
            </div>
        </button>
    );
};

/** A horizontal fill bar, as a share of `max`. */
const Bar: React.FC<{
    value: number | undefined;
    max: number;
    color: string;
    'data-testid'?: string;
}> = ({ value, max, color, 'data-testid': testId }) => {
    const colors = useFlowColors();
    const share = (v: number | undefined) => (v == null || max <= 0 ? 0 : Math.min(100, (v / max) * 100));
    return (
        <div
            data-testid={testId}
            style={{ height: 6, background: colors.hairline, borderRadius: 3, margin: '2px 0 4px' }}
        >
            <div style={{ width: `${share(value)}%`, height: '100%', background: color, borderRadius: 3 }} />
        </div>
    );
};

/** Muted caption line inside an entry card. */
const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const colors = useFlowColors();
    return <div style={{ fontSize: 11, color: colors.inkSubdued }}>{children}</div>;
};

/**
 * The dimension-specific body of an entry card.
 *
 * Productivity, staffing and flow read actual / expected with the change under it; buffer
 * also draws the capacity used as a bar, with the trend as an arrow.
 */
const EntryBody: React.FC<{ dimension: AndonDimensionKey; entry: AndonEntry; color: string; testId: string }> = ({
    dimension,
    entry,
    color,
    testId,
}) => {
    const { metric } = entry;
    const value = (text: string) => (
        <div style={{ fontSize: 20, fontWeight: 700, color }} data-testid={`${testId}-value`}>
            {text}
        </div>
    );
    const change = (text: string) => (
        <div style={{ fontSize: 13, fontWeight: 600, color }} data-testid={`${testId}-change`}>
            {text}
        </div>
    );

    if (dimension === 'flow') {
        // Read like every other card: FLOW carries the inflow as the actual and the outflow as
        // the expected, and the variance is the imbalance between the two.
        return (
            <>
                {value(formatFlowNumber(metric.actualValue))}
                <Caption>units</Caption>
                <Caption>/ {formatFlowNumber(metric.expectedValue)}</Caption>
                {change(formatVariance(metric.variancePercentage))}
            </>
        );
    }

    if (dimension === 'buffer') {
        const state = metric.bufferState;
        return (
            <>
                {value(formatFlowNumber(metric.actualValue))}
                <Caption>units</Caption>
                <Caption>/ {formatFlowNumber(metric.expectedValue)}</Caption>
                <Bar
                    value={bufferFillPercent(metric.variancePercentage)}
                    max={100}
                    color={color}
                    data-testid={`${testId}-fill`}
                />
                {state && (
                    <div
                        style={{ fontSize: 14, fontWeight: 700, color }}
                        aria-label={BUFFER_STATE_LABELS[state] ?? state}
                        title={BUFFER_STATE_LABELS[state] ?? state}
                        data-testid={`${testId}-state`}
                    >
                        {BUFFER_STATE_ARROWS[state] ?? state}
                    </div>
                )}
            </>
        );
    }

    if (dimension === 'staffing') {
        return (
            <>
                {value(formatFlowNumber(metric.actualValue))}
                <Caption>associates</Caption>
                <Caption>/ {formatFlowNumber(metric.expectedValue)}</Caption>
                {change(`${formatSignedNumber(metric.delta)} HC`)}
            </>
        );
    }

    return (
        <>
            {value(formatFlowNumber(metric.actualValue))}
            <Caption>units/hr</Caption>
            <Caption>/ {formatFlowNumber(metric.expectedValue)}</Caption>
            {change(formatVariance(metric.variancePercentage))}
        </>
    );
};

/**
 * One path's card in a dimension section, tinted and outlined by that dimension's status.
 *
 * The status is the dimension's metric status — not the path's overall health — so a path
 * that is fine on staffing but behind on UPH reads red under Productivity only. Colour is not
 * the only signal: the card's accessible name spells the status out.
 */
const EntryCard: React.FC<{ dimension: AndonDimensionKey; entry: AndonEntry }> = ({ dimension, entry }) => {
    const colors = useFlowColors();
    const status = entry.metric.comparisonStatus;
    const color = colors.statusAccent(status);
    const testId = `andon-entry-${dimension}-${entry.key}`;
    return (
        <div
            role="group"
            aria-label={`${entry.label}: ${getStatusLabel(status)}`}
            data-testid={testId}
            data-status={status}
            style={{
                flex: `1 1 ${ENTRY_CARD_MIN_WIDTH}px`,
                minWidth: ENTRY_CARD_MIN_WIDTH,
                maxWidth: ENTRY_CARD_MAX_WIDTH,
                boxSizing: 'border-box',
                padding: '12px 14px',
                textAlign: 'center',
                background: colors.statusBackground(status),
                border: `2px solid ${color}`,
                borderRadius: 4,
            }}
        >
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                {entry.label.toUpperCase()}
            </div>
            <EntryBody dimension={dimension} entry={entry} color={color} testId={testId} />
        </div>
    );
};

/**
 * One dimension's section: a header bar, then the cards.
 *
 * Only the paths above threshold — critical (red) and at risk (amber) — show by default, worst
 * first, so the section reads as a list of what needs attention. The header's "Show all N"
 * reveals every path and turns into "Show critical only" to fold the healthy ones away again.
 *
 * A dimension no path carries a metric for still gets its header, with a note in place of the
 * cards, rather than vanishing — so a missing feed reads as missing, not as a layout change.
 */
const DimensionSection: React.FC<{
    dimension: AndonDimension;
    cards?: ProcessPathCard[];
    supportPaths?: SupportPathMetrics[];
}> = ({ dimension, cards, supportPaths }) => {
    const colors = useFlowColors();
    const [showAll, setShowAll] = useState(false);
    const entries = andonEntries(dimension.key, cards, supportPaths);
    const aboveThreshold = entries.filter((entry) => isAboveThresholdStatus(entry.metric.comparisonStatus));
    const shown = showAll ? entries : aboveThreshold;

    return (
        <section aria-label={dimension.sectionTitle} data-testid={`andon-section-${dimension.key}`}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: colors.highlight,
                    borderRadius: 4,
                    padding: '8px 16px',
                    marginBottom: 8,
                }}
            >
                <h3 style={{ margin: 0, fontSize: 14, letterSpacing: 0.5 }}>
                    <span aria-hidden="true">{dimension.icon} </span>
                    {dimension.sectionTitle.toUpperCase()}
                </h3>
                {entries.length > 0 && (
                    <button
                        type="button"
                        aria-expanded={showAll}
                        onClick={() => setShowAll(!showAll)}
                        data-testid={`andon-section-${dimension.key}-toggle`}
                        style={{
                            // Highlighted while expanded, like a pressed toggle.
                            background: showAll ? colors.pressed : 'none',
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px 10px',
                            color: colors.primary,
                            fontWeight: 600,
                            cursor: 'pointer',
                            font: 'inherit',
                        }}
                    >
                        {showAll ? 'Show critical only' : `Show all ${entries.length} ▸`}
                    </button>
                )}
            </div>
            {entries.length === 0 ? (
                <Text type="b200" color="secondary" data-testid={`andon-section-${dimension.key}-no-data`}>
                    No {dimension.label.toLowerCase()} data for this interval.
                </Text>
            ) : shown.length === 0 ? (
                <Text type="b200" color="secondary" data-testid={`andon-section-${dimension.key}-healthy`}>
                    No paths above threshold — {formatFlowNumber(entries.length)} paths reporting.
                </Text>
            ) : (
                // A plain flex row rather than Meridian's Row, so the cards can grow to share it.
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }} data-testid={`andon-section-${dimension.key}-cards`}>
                    {shown.map((entry) => (
                        <EntryCard key={entry.key} dimension={dimension.key} entry={entry} />
                    ))}
                </div>
            )}
        </section>
    );
};

/**
 * The Andon cards view, organised by dimension: productivity, staffing, flow and buffer.
 *
 * A row of dimension tiles comes first — each the dimension's healthy share — with a legend
 * saying what healthy means for each. Below, each dimension has its own section listing the
 * paths that need attention. Pressing a tile narrows the view to that dimension's section.
 *
 * Tiles come from the service's `andonSummary` and sections from the cards, so the cards
 * still render when the summary is missing — only the tiles and the filter drop out.
 */
const AndonCardsView: React.FC<AndonCardsViewProps> = ({ cards, andonSummary, supportPaths }) => {
    const colors = useFlowColors();
    const [selected, setSelected] = useState<AndonDimensionKey | null>(null);
    const tiles = andonSummary ? ANDON_DIMENSIONS.filter((dimension) => andonSummary[dimension.key] != null) : [];
    const sections = ANDON_DIMENSIONS.filter((dimension) => selected == null || dimension.key === selected);
    const hasEntries = ANDON_DIMENSIONS.some(
        (dimension) => andonEntries(dimension.key, cards, supportPaths).length > 0
    );

    return (
        <Column spacing="400" data-testid="flow-map-andon-view">
            {tiles.length > 0 && andonSummary && (
                <Column spacing="200">
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {tiles.map((dimension) => (
                            <DimensionTile
                                key={dimension.key}
                                dimension={dimension}
                                andonSummary={andonSummary}
                                selected={selected === dimension.key}
                                onToggle={() => setSelected(selected === dimension.key ? null : dimension.key)}
                            />
                        ))}
                    </div>
                    <div
                        style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: colors.inkSubdued }}
                        data-testid="andon-legend"
                    >
                        {tiles.map((dimension) => (
                            <span key={dimension.key}>
                                <span aria-hidden="true">{dimension.icon} </span>
                                <strong>{dimension.label}:</strong> Healthy = {dimension.healthyMeans}
                            </span>
                        ))}
                        <span data-testid="andon-legend-reading">
                            <strong>{ACTUAL_VS_EXPECTED_LEGEND.sample}</strong> = {ACTUAL_VS_EXPECTED_LEGEND.meaning}
                        </span>
                    </div>
                </Column>
            )}

            {hasEntries ? (
                sections.map((dimension) => (
                    <DimensionSection
                        key={dimension.key}
                        dimension={dimension}
                        cards={cards}
                        supportPaths={supportPaths}
                    />
                ))
            ) : (
                <Text type="b300" color="secondary" data-testid="andon-cards-empty">
                    No process path cards for this interval.
                </Text>
            )}
        </Column>
    );
};

export default AndonCardsView;
